import FileUpload from "components/FileUpload";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import React, { useState, useRef, useEffect } from "react";
import { createLegalRespondbackFeedback, updateLegalAdvisory } from "services/legalAdvisoryService";
import { createLegislativeRespondbackFeedback, updateLegislativeDrafting } from "services/legislativeService";
import { LEGAL_RESEARCH_REQUEST_STATUS, LEGISLATIVE_DRAFTING_REQUEST_STATUS } from "utils/constantValues";
import { getUserType } from "utils/helpers";
import { useTranslation } from "react-i18next";

interface uploadProposalProps {
    request: any;
    isOpen: boolean;
    onClose: () => void;
    setLegalResearch: Function;
    forward: any;
    type?: string;
    feedbackType?: string;
}

const LegislativeRespondBackForm: React.FC<uploadProposalProps> = ({
    request,
    isOpen,
    setLegalResearch,
    onClose,
    forward,
    type,
    feedbackType,
}) => {
    const { t } = useTranslation();
    const [proposalSummary, setProposalSummary] = useState(request.proposal_summary || "");
    const [proposalFile, setProposalFile] = useState();
    const { showToast } = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const contextData = useAuth();

    // Create a ref for the modal content div
    const modalContentRef = useRef<HTMLDivElement>(null);

    // Effect to handle clicks outside the modal
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                modalContentRef.current &&
                !modalContentRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setValidationError(null);
        setSubmitting(true);

        try {
            const data = new FormData();
            if (type === "legal")
                data.append("advisory_request", request.id);
            else
                data.append("instruction", request.id);
            data.append("role", getUserType(contextData.user?.roles[0]));
            data.append("summary", proposalSummary);
            if (feedbackType)
                data.append("feedback_type", feedbackType);
            else
                data.append("feedback_type", "other");
            data.append("given_by", contextData?.user?.full_name || "");
            if (proposalFile)
                data.append("respond_back_files", proposalFile ?? null);

            let response;
            if (type === "legal")
                response = await createLegalRespondbackFeedback(data);
            else
                response = await createLegislativeRespondbackFeedback(data);
            
            const researchData = new FormData();
            if (type === "legal")
                researchData.append("status", forward);
            else
                researchData.append("current_status", forward);
            
            if (feedbackType === "Audit")
                researchData.append("forward_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_coordinator.value);
            else if (feedbackType)
                researchData.append("forward_status", LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value);
            if (feedbackType && request.forward_status === LEGISLATIVE_DRAFTING_REQUEST_STATUS.forwarded_to_coordinator.value)
                researchData.append("forward_status", LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value);
            
            let researchResponse: any;
            if (type === "legal")
                researchResponse = await updateLegalAdvisory(request.id, researchData);
            else
                researchResponse = await updateLegislativeDrafting(request.id, researchData);
            
            setLegalResearch((prev: any) => ({
                ...prev,
                ...researchResponse,
            }));
            
            onClose();
            showToast(t("RespondBackSubmitted") || "Respond Back Submitted", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUploadFile") || "Failed to upload file.", "error");
        } finally {
            setSubmitting(false);
        }

        onClose();
    };

    const handleFileChange = (
        file: any,
    ) => {
        setProposalFile(file);
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-end p-4 z-50 overflow-hidden">
            <div
                ref={modalContentRef}
                className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative flex flex-col h-full my-auto overflow-y-auto pr-2 custom-scrollbar"
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                    {t("RespondBack") || "Respond Back"}
                </h2>

                <form onSubmit={handleSubmit} className="flex flex-col">
                    <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar">
                        <div className="mb-4">
                            <label
                                htmlFor="remark"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {t("RespondBackFeedback") || "Respond Back Feedback"}
                            </label>
                            <textarea
                                id="remark"
                                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[100px]"
                                placeholder={t("DetailHere") || "Detail here..."}
                                value={proposalSummary}
                                onChange={(e) => setProposalSummary(e.target.value)}
                            ></textarea>
                        </div>

                        <div className="mb-6">
                            <label
                                htmlFor="deadline"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {t("RespondBackFile") || "Respond Back File"}
                            </label>
                            <div className="relative">
                                <FileUpload onChange={handleFileChange} />
                            </div>
                        </div>
                    </div>
                    
                    {validationError && (
                        <p className="text-red-600 text-sm mb-4">{validationError}</p>
                    )}
                    
                    <div className="flex space-x-4 pt-4 border-t border-gray-200">
                        <button
                            type="submit"
                            className="w-full bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-6 rounded-md disabled:opacity-70"
                            disabled={submitting}
                        >
                            {submitting ? (t("Submitting") || "Submitting...") : (t("Submit") || "Submit")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LegislativeRespondBackForm;